'use client';

import * as React from 'react';
import { FilterSidebar } from './FilterSidebar';
import { CandidateCard } from './CandidateCard';
import { CandidateSearchBanner } from './CandidateSearchBanner';
import { Search, SlidersHorizontal, Loader2, X, ChevronLeft, ChevronRight, ListFilter, Lock, PartyPopper } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { useDebounce } from '../../hooks/use-debounce';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/doi-tac-module/hooks/use-toast";
import { cn } from '@/doi-tac-module/lib/utils';
import {
    type SearchFilters,
    initialSearchFilters,
    keyMap,
    parseSearchParams
} from '@/doi-tac-module/lib/candidate-filter-utils';
import { countCandidates } from '@/doi-tac-module/actions/candidate-search';

import { useLanguage } from '../../contexts/LanguageContext';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';
import { getUserInteractions, toggleCandidateInteraction } from '@/doi-tac-module/actions/candidate-interactions';
import Link from 'next/link';

export function CandidateSearchClient({
    initialCandidates,
    totalCount,
    currentPage = 1,
    totalPages = 1,
    agencies = [],
    currentUser,
    contactPool,
    isTrash = false,
    isAgency = false,
    initialInteractions = { saved: [], invited: [] }
}: {
    initialCandidates: any[],
    totalCount: number,
    currentPage?: number,
    totalPages?: number,
    agencies?: any[],
    currentUser?: any,
    contactPool?: { rule: 'random' | 'round-robin'; pool: { profile: any; weight: number }[] } | null,
    isTrash?: boolean,
    isAgency?: boolean,
    initialInteractions?: { saved: string[], invited: string[] }
}) {
    const { language } = useLanguage();
    const t = agencyTranslations;
    const router = useRouter();
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const { toast } = useToast();

    const [isCounting, setIsCounting] = React.useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
    const [showInviteSuccessDialog, setShowInviteSuccessDialog] = React.useState(false);
    const [sortBy, setSortBy] = React.useState<string>(searchParams.get('sap-xep') || 'moi-nhat');

    // Khởi tạo filter trực tiếp từ URL để tránh flicker count hoặc sai lệch count ban đầu
    const initialFiltersFromURL = React.useMemo(() => {
        const paramsObj: any = {};
        searchParams.forEach((value, key) => {
            if (paramsObj[key]) {
                if (Array.isArray(paramsObj[key])) {
                    paramsObj[key].push(value);
                } else {
                    paramsObj[key] = [paramsObj[key], value];
                }
            } else {
                paramsObj[key] = value;
            }
        });
        return parseSearchParams(paramsObj);
    }, [searchParams]);

    const [appliedFilters, setAppliedFilters] = React.useState<SearchFilters>(initialFiltersFromURL);
    const [stagedFilters, setStagedFilters] = React.useState<SearchFilters>(initialFiltersFromURL);
    const [stagedResultCount, setStagedResultCount] = React.useState<number>(totalCount);

    const [interactions, setInteractions] = React.useState<{ saved: string[], invited: string[] }>(initialInteractions);

    // MOIPHONGVAN001: Auto-invite effect
    React.useEffect(() => {
        const autoInviteId = searchParams.get('autoInvite');
        if (autoInviteId) {
            // Tự động scroll đến thẻ ID của ứng viên
            setTimeout(() => {
                const el = document.getElementById(autoInviteId);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el.classList.add('ring-4', 'ring-[#AFC536]', 'transition-all', 'duration-500');
                    setTimeout(() => el.classList.remove('ring-4', 'ring-[#AFC536]'), 2000);
                }
            }, 500);

            // Tự động gọi thay mặt user
            toggleCandidateInteraction(autoInviteId, 'invited').then(res => {
                if (res.success && res.action === 'added') {
                    toast({
                        title: "Đã mời phỏng vấn",
                        description: `Gửi lời mời phỏng vấn đến ứng viên ${autoInviteId} thành công.`,
                    });
                    
                    setInteractions(prev => {
                        const list = [...prev.invited];
                        if (!list.includes(autoInviteId)) list.push(autoInviteId);
                        return { ...prev, invited: list };
                    });

                    // Hiển thị Dialog hỏi tiếp theo
                    setShowInviteSuccessDialog(true);
                } else if (!res.success) {
                    toast({
                        variant: "destructive",
                        title: "Lỗi",
                        description: res.error || "Không thể thực hiện lời mời phỏng vấn lúc này.",
                    });
                }
            });

            // Xoá param khỏi URL để tránh trigger nhầm khi re-render
            const url = new URL(window.location.href);
            url.searchParams.delete('autoInvite');
            window.history.replaceState({}, '', url.toString());
        }
    }, [searchParams, toast]);

    // Keep state in sync with server-provided props (essential for navigation between pages)
    React.useEffect(() => {
        if (JSON.stringify(initialInteractions) !== JSON.stringify(interactions)) {
            setInteractions(initialInteractions);
        }
    }, [initialInteractions]);

    const viewMode = React.useMemo(() => {
        if (pathname.includes('da-luu')) return 'saved';
        if (pathname.includes('da-moi')) return 'invited';
        return 'search';
    }, [pathname]);

    // Sync state when URL params or totalCount from server changes (direct navigation/back/forward)
    React.useEffect(() => {
        if (JSON.stringify(initialFiltersFromURL) !== JSON.stringify(appliedFilters)) {
            setAppliedFilters(initialFiltersFromURL);
        }
        if (JSON.stringify(initialFiltersFromURL) !== JSON.stringify(stagedFilters)) {
            setStagedFilters(initialFiltersFromURL);
        }
        if (totalCount !== stagedResultCount) {
            setStagedResultCount(totalCount);
        }
        const currentSort = searchParams.get('sap-xep') || 'moi-nhat';
        if (currentSort !== sortBy) {
            setSortBy(currentSort);
        }
    }, [initialFiltersFromURL, totalCount, searchParams]);

    // Handle debounced counting
    const debouncedStagedFilters = useDebounce(stagedFilters, 500);

    React.useEffect(() => {
        const fetchCount = async () => {
            setIsCounting(true);
            const countPayload = { ...debouncedStagedFilters };
            if (viewMode === 'saved') {
                countPayload.ids = interactions.saved;
            } else if (viewMode === 'invited') {
                countPayload.ids = interactions.invited;
            }
            const res = await countCandidates(countPayload, currentUser);
            if (res.success && res.total !== stagedResultCount) {
                setStagedResultCount(res.total);
            }
            setIsCounting(false);
        };

        // Only fetch if filters have actually changed from applied, OR if interactions changed in a shortcut mode
        if (JSON.stringify(debouncedStagedFilters) !== JSON.stringify(appliedFilters) || viewMode !== 'search') {
            fetchCount();
        } else {
            setStagedResultCount(totalCount);
        }
    }, [debouncedStagedFilters, appliedFilters, totalCount, viewMode, interactions]);

    const handleFilterChange = React.useCallback((newFilters: Partial<SearchFilters>) => {
        setStagedFilters(prev => ({ ...prev, ...newFilters }));
    }, []);

    const handleApplyFilters = React.useCallback(() => {
        const params = new URLSearchParams();
        const defaults = initialSearchFilters as any;
        Object.entries(stagedFilters).forEach(([key, value]) => {
            const urlKey = keyMap[key] || key;
            // Skip empty / falsy
            if (!value && value !== 0) return;
            if (Array.isArray(value) && value.length === 0) return;
            // Skip if same as default for range arrays (age, height, weight, yearsOfExperience)
            if (Array.isArray(value) && Array.isArray(defaults[key])) {
                const isDefault = JSON.stringify(value) === JSON.stringify(defaults[key]);
                if (isDefault) return;
                value.forEach((v: any) => params.append(urlKey, String(v)));
                return;
            }
            if (Array.isArray(value)) {
                value.forEach((v: any) => params.append(urlKey, String(v)));
            } else if (value !== defaults[key]) {
                params.set(urlKey, String(value));
            }
        });
        if (sortBy !== 'moi-nhat') {
            params.set('sap-xep', sortBy);
        }

        setIsSidebarOpen(false);
        const currentPath = window.location.pathname;
        router.push(`${currentPath}?${params.toString()}`);
    }, [stagedFilters, sortBy, router]);

    const handleResetFilters = React.useCallback(() => {
        setStagedFilters(initialSearchFilters);
        const currentPath = window.location.pathname;
        router.push(currentPath);
    }, [router]);

    const handlePageChange = (newPage: number) => {
        if (newPage < 1 || newPage > totalPages) return;
        const newParams = new URLSearchParams(searchParams.toString());
        newParams.set('page', newPage.toString());
        const currentPath = window.location.pathname;
        router.push(`${currentPath}?${newParams.toString()}`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSortChange = (value: string) => {
        setSortBy(value);
        const params = new URLSearchParams(searchParams.toString());
        if (value === 'moi-nhat') {
            params.delete('sap-xep');
        } else {
            params.set('sap-xep', value);
        }
        params.delete('page'); // Reset to page 1 on sort change
        const currentPath = window.location.pathname;
        router.push(`${currentPath}?${params.toString()}`);
    };

    const handleKeywordSearch = () => {
        // Reset page to 1 when doing a new search
        const newParams = new URLSearchParams(searchParams.toString());
        newParams.delete('page');

        // Push the new search
        handleApplyFilters();
    };

    const liveTotalCount = React.useMemo(() => {
        if (viewMode === 'search') return totalCount;
        let removed = 0;
        initialCandidates.forEach(cand => {
            if (viewMode === 'saved' && !interactions.saved.includes(cand.id)) removed++;
            if (viewMode === 'invited' && !interactions.invited.includes(cand.id)) removed++;
        });
        return Math.max(0, totalCount - removed);
    }, [totalCount, viewMode, interactions, initialCandidates]);

    const showExpAndLang = true; // Always show exp and lang as requested

    return (
        <div className={cn(isTrash && "grayscale opacity-90")}>
            <CandidateSearchBanner
                filters={stagedFilters}
                onFilterChange={handleFilterChange}
                onSearch={handleKeywordSearch}
                isAgency={isAgency}
            />
            <div className="container mx-auto px-4 max-w-screen-2xl mt-6 md:mt-8">
                <div className="flex flex-col lg:flex-row gap-6">

                    {/* Desktop Sidebar */}
                    <div className="hidden lg:flex w-[300px] shrink-0 flex-col">
                        <FilterSidebar
                            filters={stagedFilters}
                            appliedFilters={appliedFilters}
                            onFilterChange={handleFilterChange}
                            onApply={handleApplyFilters}
                            onReset={handleResetFilters}
                            resultCount={stagedResultCount}
                            isCounting={isCounting}
                            agencies={agencies}
                            currentUser={currentUser}
                        />
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 space-y-5">
                        {/* Search Header */}
                        <div className="flex justify-between items-center mb-4 px-1">
                            <h2 className="text-xl font-bold text-slate-800">
                                {t.candidateSearch?.results?.title?.[language] || 'Results'} ({liveTotalCount})
                            </h2>
                            <div className="flex items-center gap-2">
                                {/* Mobile Filter Trigger integrated here */}
                                <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
                                    <SheetTrigger asChild>
                                        <Button variant="ghost" size="sm" className="flex items-center gap-1 lg:hidden">
                                            <ListFilter className="w-4 h-4" />
                                            {t.candidateSearch?.filters?.titles?.candidateFilters?.[language] || 'Filters'}
                                        </Button>
                                    </SheetTrigger>
                                    <SheetContent side="left" className="w-full sm:max-w-md p-0 border-none">
                                        <div className="h-full flex flex-col">
                                            <SheetHeader className="p-4 border-b bg-white sticky top-0 z-10">
                                                <div className="flex justify-between items-center">
                                                    <SheetTitle className="text-lg font-bold">
                                                        {t.candidateSearch?.card?.advancedFilterLabel?.[language] || 'Advanced Filters'}
                                                    </SheetTitle>
                                                    <Button variant="ghost" size="icon" onClick={() => setIsSidebarOpen(false)} className="rounded-full">
                                                        <X className="h-5 w-5" />
                                                    </Button>
                                                </div>
                                            </SheetHeader>
                                            <div className="flex-1 overflow-auto flex flex-col">
                                                <FilterSidebar
                                                    filters={stagedFilters}
                                                    appliedFilters={appliedFilters}
                                                    onFilterChange={handleFilterChange}
                                                    onApply={handleApplyFilters}
                                                    onReset={handleResetFilters}
                                                    resultCount={stagedResultCount}
                                                    isCounting={isCounting}
                                                    agencies={agencies}
                                                    currentUser={currentUser}
                                                />
                                            </div>
                                        </div>
                                    </SheetContent>
                                </Sheet>

                                <Select value={sortBy} onValueChange={handleSortChange}>
                                    <SelectTrigger id="SAPXEP_CANDIDATE" className="w-auto lg:w-[180px] bg-white border-slate-200 rounded-full h-10 shadow-sm text-sm font-medium">
                                        <SelectValue placeholder={t.candidateSearch?.sort?.placeholder?.[language] || 'Sort by'} />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl border-slate-100 shadow-xl">
                                        <SelectItem value="moi-nhat">{t.candidateSearch?.sort?.options?.newest?.[language] || 'Newest'}</SelectItem>
                                        <SelectItem value="luong-co-ban-cao-den-thap">{t.candidateSearch?.sort?.options?.salaryHigh?.[language] || 'Salary High'}</SelectItem>
                                        <SelectItem value="luong-co-ban-thap-den-cao">{t.candidateSearch?.sort?.options?.salaryLow?.[language] || 'Salary Low'}</SelectItem>
                                        <SelectItem value="thuc-linh-cao-den-thap">{t.candidateSearch?.sort?.options?.netSalaryHigh?.[language] || 'Net Salary High'}</SelectItem>
                                        <SelectItem value="thuc-linh-thap-den-cao">{t.candidateSearch?.sort?.options?.netSalaryLow?.[language] || 'Net Salary Low'}</SelectItem>
                                        <SelectItem value="uu-tien-co-anh">{t.candidateSearch?.sort?.options?.priorityPhoto?.[language] || 'Priority Photo'}</SelectItem>
                                        <SelectItem value="uu-tien-co-video">{t.candidateSearch?.sort?.options?.priorityVideo?.[language] || 'Priority Video'}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Candidate List */}
                        <div className="grid grid-cols-1 gap-4 relative">
                            {React.useMemo(() => {
                                if (!currentUser) {
                                    const previewAvatars = initialCandidates
                                        .filter(c => c.avatarUrl)
                                        .slice(0, 5)
                                        .map(c => c.avatarUrl);

                                    return (
                                        <div className="relative w-full">
                                            {/* Blurred Placeholder Candidates - Show 20 or actual count */}
                                            <div className="w-full space-y-4 blur-[5px] opacity-40 pointer-events-none select-none">
                                                {initialCandidates.map((candidate, idx) => (
                                                    <CandidateCard
                                                        key={candidate.id || idx}
                                                        candidate={candidate as any}
                                                        showExpAndLang={showExpAndLang}
                                                        agencies={agencies}
                                                        contactPool={contactPool}
                                                        isSaved={false}
                                                        isInvited={false}
                                                        viewMode="search"
                                                        onInteractionChange={() => {}}
                                                    />
                                                ))}
                                            </div>

                                            {/* Sticky Unauthorized Overlay */}
                                            <div className="absolute inset-0 pointer-events-none flex flex-col items-center">
                                                <div className="sticky top-1/2 -translate-y-1/2 pointer-events-auto z-50 w-full max-w-lg px-4">
                                                    <div className="bg-white/95 backdrop-blur-xl border border-white/50 rounded-[2rem] p-6 md:p-8 text-center shadow-[0_30px_80px_rgba(25,166,223,0.12)] relative overflow-hidden group border-b-4 border-b-[#19A6DF]/10">
                                                        {/* Decorative Elements */}
                                                        <div className="absolute -top-24 -right-24 w-48 h-48 bg-[#19A6DF]/10 rounded-full blur-3xl group-hover:bg-[#19A6DF]/20 transition-all duration-700" />
                                                        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-[#19A6DF]/10 rounded-full blur-3xl group-hover:bg-[#19A6DF]/20 transition-all duration-700" />
                                                        
                                                        <div className="relative z-10 flex flex-col items-center">
                                                            <div className="bg-gradient-to-br from-[#19A6DF] to-[#19A6DF]/70 w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-[#19A6DF]/20 transform rotate-3 group-hover:rotate-0 transition-transform duration-500 ring-2 ring-white">
                                                                <Lock className="h-7 w-7 text-white" />
                                                            </div>
                                                            
                                                            <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-2 tracking-tight font-headline whitespace-nowrap">
                                                                {t.candidateSearch?.results?.unauthorized?.title?.[language] || 'Đăng nhập để xem ứng viên'}
                                                            </h3>
                                                            
                                                            <p className="text-slate-500 text-[13px] md:text-sm font-normal leading-relaxed mb-6 max-w-[320px] mx-auto px-2">
                                                                {t.candidateSearch?.results?.unauthorized?.description?.[language] || 'Vui lòng đăng nhập hoặc đăng ký tài khoản ngay để xem danh sách ứng viên.'}
                                                            </p>
                                                            
                                                            <Link 
                                                                href={`/auth?redirect=${encodeURIComponent(pathname)}&scroll=${typeof window !== 'undefined' ? window.scrollY : 0}`} 
                                                                className="w-full sm:w-auto"
                                                                onClick={(e) => {
                                                                    const scroll = window.scrollY;
                                                                    setNavigatingPath(`/auth?redirect=${encodeURIComponent(pathname)}&scroll=${scroll}`);
                                                                }}
                                                            >
                                                                <Button 
                                                                    size="lg" 
                                                                    className="w-full sm:w-auto rounded-2xl px-10 bg-[#19A6DF] hover:bg-[#19A6DF]/90 text-white font-medium h-12 shadow-xl shadow-[#19A6DF]/20 hover:shadow-[#19A6DF]/30 transition-all active:scale-95 text-base"
                                                                >
                                                                    {t.candidateSearch?.results?.unauthorized?.cta?.[language] || 'Đăng nhập / Đăng ký ngay'}
                                                                </Button>
                                                            </Link>

                                                            <div className="mt-6 flex flex-col items-center gap-3">
                                                                <div className="flex -space-x-3">
                                                                    {previewAvatars.length > 0 ? (
                                                                        previewAvatars.map((url, i) => (
                                                                            <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-slate-100 overflow-hidden shadow-md transform hover:-translate-y-1 transition-transform">
                                                                                <img src={url} alt="Candidate" className="w-full h-full object-cover opacity-90" />
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        [1, 2, 3, 4, 5].map(i => (
                                                                            <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-slate-100 shadow-md" />
                                                                        ))
                                                                    )}
                                                                </div>
                                                                <div className="flex flex-col items-center">
                                                                    <span className="text-lg font-bold text-[#AFC536]">
                                                                        {totalCount.toLocaleString()}+ ứng viên
                                                                    </span>
                                                                    <span className="text-[11px] font-normal text-slate-400 capitalize tracking-wide">
                                                                        Chất lượng cao đang chờ bạn
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                return initialCandidates.length > 0 ? (
                                    initialCandidates.map((candidate, idx) => (
                                        <CandidateCard
                                            key={`candidate-${candidate.id || idx}-${idx}`}
                                            candidate={candidate}
                                            showExpAndLang={showExpAndLang}
                                            agencies={agencies}
                                            contactPool={contactPool}
                                            isTrash={isTrash}
                                            isSaved={interactions.saved.includes(candidate.id)}
                                            isInvited={interactions.invited.includes(candidate.id)}
                                            viewMode={viewMode}
                                            onInteractionChange={(id, type, action) => {
                                                setInteractions(prev => {
                                                    const list = [...prev[type]];
                                                    if (action === 'added') {
                                                        if (!list.includes(id)) list.push(id);
                                                    } else {
                                                        const idx = list.indexOf(id);
                                                        if (idx > -1) list.splice(idx, 1);
                                                    }
                                                    return { ...prev, [type]: list };
                                                });
                                            }}
                                        />
                                    ))
                                ) : (
                                    <div className="bg-white border border-slate-100 rounded-xl p-20 text-center shadow-sm">
                                        <div className="bg-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Search className="h-8 w-8 text-slate-300" />
                                        </div>
                                        <h3 className="font-bold text-slate-700">
                                            {t.candidateSearch?.results?.noResults?.[language] || 'No Results'}
                                        </h3>
                                        <p className="text-sm text-muted-foreground max-w-xs mx-auto mt-1">
                                            {t.candidateSearch?.results?.noResultsDesc?.[language] || ''}
                                        </p>
                                        <Button variant="outline" className="mt-6 rounded-full" onClick={handleResetFilters}>
                                            {t.candidateSearch?.results?.resetFilters?.[language] || 'Reset'}
                                        </Button>
                                    </div>
                                );
                            }, [initialCandidates, showExpAndLang, handleResetFilters, isTrash, contactPool, currentUser, pathname, language, t, interactions, agencies, viewMode])}
                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center py-6 gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={currentPage <= 1}
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    className="h-10 px-4 rounded-full"
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" /> {t.candidateSearch?.results?.pagination?.prev?.[language] || 'Prev'}
                                </Button>
                                <div className="flex items-center gap-1 mx-2">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                                        .map((p, idx, arr) => (
                                            <React.Fragment key={`page-wrapper-${p}`}>
                                                {idx > 0 && arr[idx - 1] !== p - 1 && (
                                                    <span key={`ellipsis-${p}-${idx}`} className="text-muted-foreground px-2">...</span>
                                                )}
                                                <Button
                                                    key={`page-btn-${p}`}
                                                    variant={p === currentPage ? "default" : "ghost"}
                                                    size="sm"
                                                    onClick={() => handlePageChange(p)}
                                                    className={`w-10 h-10 rounded-full p-0 font-medium ${p === currentPage
                                                        ? (isAgency ? 'bg-[#FF6921] text-white shadow-sm hover:bg-[#FF6921]/90' : 'bg-primary text-white shadow-sm hover:bg-primary/90')
                                                        : 'text-slate-600 hover:bg-slate-100'
                                                        }`}
                                                >
                                                    {p}
                                                </Button>
                                            </React.Fragment>
                                        ))}
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={currentPage >= totalPages}
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    className="h-10 px-4 rounded-full"
                                >
                                    {t.candidateSearch?.results?.pagination?.next?.[language] || 'Next'} <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* MOIPHONGVAN001: Dialog hỏi sau khi auto-invite */}
            <AlertDialog open={showInviteSuccessDialog} onOpenChange={setShowInviteSuccessDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-slate-900">
                            <PartyPopper className="w-5 h-5 text-[#F2B92A]" />
                            Mời phỏng vấn thành công!
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-600 leading-relaxed">
                            Hệ thống đã gửi thông báo mời phỏng vấn đến ứng viên thành công. Bạn muốn tiếp tục khám phá thêm các nhân sự tài năng khác tại đây, hay muốn kiểm tra danh sách <strong>các ứng viên đã mời</strong>?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setShowInviteSuccessDialog(false)}>Tiếp tục Mời</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-[#19A6DF] hover:bg-[#158bbd] text-white"
                            onClick={() => {
                                setShowInviteSuccessDialog(false);
                                router.push('/ung-vien-da-moi');
                            }}
                        >
                            Ứng viên đã mời
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
